import axios from "axios";
import { rootStore } from "@/mobx/store";
import { API_URL, HEADER, RESULT_CODE } from "@/config/const";
import {
  refreshAccessToken,
  sanitizeToken,
  isTokenExpiringSoon,
} from "@/utils/tokenRefresh";
import {
  isTransientGetError,
  MAX_TRANSIENT_GET_RETRIES,
  transientGetRetryDelay,
} from "@/config/api_retry";
import {
  buildApiFailureContext,
  createApiTraceId,
  enqueueApiFailure,
  takeApiFailures,
} from "@/config/apiTelemetry";

/**
 * ✅ 중복 요청 방지(강제)
 * - 같은 method + url + params + data 요청이 "진행 중"이면,
 *   새 요청을 "전송 전에" Cancel 처리해서 네트워크 탭에 안 찍히게 함.
 *
 * 주의:
 * - GET까지 막고 싶지 않으면 아래 shouldDedupe에서 GET 제외 가능
 */
const pendingRequestKeys = new Set<string>();

const stableStringify = (value: any) => {
  try {
    if (value == null) return "";
    if (typeof value === "string") return value;

    const seen = new WeakSet();
    return JSON.stringify(value, (key, val) => {
      if (val && typeof val === "object") {
        if (seen.has(val)) return;
        seen.add(val);

        // object key 정렬
        if (!Array.isArray(val)) {
          return Object.keys(val)
            .sort()
            .reduce((acc: any, k) => {
              acc[k] = (val as any)[k];
              return acc;
            }, {});
        }
      }
      return val;
    });
  } catch {
    return "";
  }
};

/**
 * FormData 는 JSON.stringify 하면 항상 "{}" 라 모든 업로드가 같은 키로 묶였다 — 두 번째 파일을
 * 올리면 중복으로 취소되고 호출부는 영원히 대기했다(생기부 분석 업로드). 항목 이름과 파일의
 * 이름·크기·수정시각으로 키를 만든다. 내용까지 읽지는 않는다(키 계산이 동기여야 한다).
 */
export const describeRequestData = (data: any): string => {
  if (typeof FormData !== "undefined" && data instanceof FormData) {
    const parts: string[] = [];
    data.forEach((value, name) => {
      if (typeof value === "string") {
        parts.push(`${name}=${value}`);
      } else {
        const file = value as File;
        parts.push(`${name}=file:${file.name}:${file.size}:${file.lastModified ?? 0}`);
      }
    });
    return `formdata(${parts.sort().join("&")})`;
  }
  return stableStringify(data);
};

const buildRequestKey = (config: any) => {
  const method = (config.method || "get").toLowerCase();
  const url = config.baseURL
    ? `${config.baseURL}${config.url || ""}`
    : config.url || "";
  const params = stableStringify(config.params);
  const data = describeRequestData(config.data);

  // retry는 별도 키로 취급(막히면 재시도 자체가 안 되니까)
  const retryTag = config._retry ? ":retry" : "";

  return `${method}:${url}?p=${params}&d=${data}${retryTag}`;
};

const shouldDedupe = (config: any) => {
  // refresh는 제외
  if (config.url?.includes("/auth/refresh")) return false;

  // GET(및 기타 idempotent 조회)은 중복 차단 대상에서 제외한다.
  // 중복 차단의 목적은 POST/PUT/DELETE 연타로 인한 중복 데이터 생성/변경 방지다.
  // GET 까지 막으면, 서로 다른 컴포넌트가 같은 엔드포인트를 동시에 조회할 때
  // (예: 사이드바 + 메시지발송 화면이 둘 다 GET /corp/{id}/sender-registration/me 호출)
  // 두 번째 요청이 CanceledError 로 잘리고, 응답 에러 인터셉터가 cancel 을
  // `new Promise(() => {})`(영구 미해결)로 삼켜 호출부의 cb/errCb 가 영영 안 불린다.
  // GET 은 idempotent 하므로 중복 전송돼도 무해하다 → 차단하지 않는다.
  const method = (config.method || "get").toLowerCase();
  if (method === "get") return false;

  return true;
};

// axios 인스턴스 생성
const api = axios.create({
  baseURL: `${API_URL}/app/v1/`,
  headers: {
    [HEADER.LANG]: "ko",
  },
});

let telemetryFlushInFlight = false;

const flushApiFailures = () => {
  if (telemetryFlushInFlight || !rootStore.token) return;
  const events = takeApiFailures();
  if (events.length === 0) return;

  telemetryFlushInFlight = true;
  void axios
    .post(
      `${API_URL}/app/v1/monitoring/client-errors`,
      { events },
      {
        timeout: 5000,
        headers: {
          [HEADER.AUTH_TOKEN]: rootStore.token,
          [HEADER.LANG]: "ko",
          "X-Trace-Id": createApiTraceId(),
        },
      }
    )
    .catch(() => events.forEach((event) => enqueueApiFailure(event)))
    .finally(() => {
      telemetryFlushInFlight = false;
    });
};

// ===========================
// Request Interceptor
// ===========================
api.interceptors.request.use(
  async (config: any) => {
    config._traceId = config._traceId || createApiTraceId();
    config.headers = config.headers || {};
    config.headers["X-Trace-Id"] = config._traceId;

    // ✅ 중복 요청 방지: 여기서 "전송 전에" 잘라야 네트워크 탭에 안 찍힘
    if (shouldDedupe(config)) {
      const requestKey = buildRequestKey(config);
      config._requestKey = requestKey;

      if (pendingRequestKeys.has(requestKey)) {
        // 여기서 바로 reject => 네트워크로 안 나감
        return Promise.reject(
          new axios.CanceledError("DUPLICATE_REQUEST_BLOCKED")
        );
      }

      pendingRequestKeys.add(requestKey);
    }

    // refresh endpoint는 토큰 없이 요청(원래 로직 유지)
    if (config.url?.includes("/auth/refresh")) {
      return config;
    }

    const currentToken = rootStore.token;
    const refreshToken = localStorage.getItem("refresh_token");

    // 토큰이 없으면 그냥 진행
    if (!currentToken) {
      return config;
    }

    // 토큰이 만료 임박이면 미리 갱신
    if (isTokenExpiringSoon(currentToken) && refreshToken) {
      try {
        const newToken = await refreshAccessToken();
        config.headers = config.headers || {};
        config.headers[HEADER.AUTH_TOKEN] = newToken;
        return config;
      } catch {
        config.headers = config.headers || {};
        config.headers[HEADER.AUTH_TOKEN] = currentToken;
        return config;
      }
    }

    // 토큰이 유효하면 그대로 사용
    config.headers = config.headers || {};
    config.headers[HEADER.AUTH_TOKEN] = currentToken;
    return config;
  },
  (err) => Promise.reject(err)
);

// ===========================
// Response Interceptor
// ===========================
api.interceptors.response.use(
  async (res: any) => {
    // ✅ pending 해제
    const requestKey = (res.config as any)?._requestKey;
    if (requestKey) pendingRequestKeys.delete(requestKey);

    const resultCode = res.data?.result_code;

    // 토큰 만료(result_code 기반)
    if (
      resultCode === RESULT_CODE.TOKEN_EXPIRED &&
      !(res.config as any)._retry
    ) {
      (res.config as any)._retry = true;

      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) {
        rootStore.signOut();
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
        return new Promise(() => {});
      }

      try {
        const newToken = await refreshAccessToken();
        res.config.headers = res.config.headers || {};
        res.config.headers[HEADER.AUTH_TOKEN] = newToken;

        // retry는 새 요청이니까 key 재생성되게 제거
        delete (res.config as any)._requestKey;

        return api(res.config);
      } catch {
        rootStore.signOut();
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
        return new Promise(() => {});
      }
    }

    // EMPTY_TOKEN
    if (resultCode === RESULT_CODE.EMPTY_TOKEN) {
      rootStore.signOut();
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
      return new Promise(() => {});
    }

    // SUCCESS면 토큰 저장
    if (resultCode === RESULT_CODE.SUCCESS) {
      const newToken =
        res.data.result_data?.accessToken || res.data.accessToken;
      const newRefreshToken =
        res.data.result_data?.refreshToken || res.data.refreshToken;

      if (newToken) {
        const sanitizedToken = sanitizeToken(newToken);
        if (sanitizedToken) {
          rootStore.setToken(sanitizedToken);
        }
      }

      if (newRefreshToken) {
        const sanitizedRefreshToken = sanitizeToken(newRefreshToken);
        if (sanitizedRefreshToken) {
          localStorage.setItem("refresh_token", sanitizedRefreshToken);
        }
      }
    }

    flushApiFailures();

    return res;
  },
  async (err: any) => {
    const originalRequest = err.config;

    // ✅ 중복 요청 차단/사용자 연타로 인한 cancel은 조용히 무시
    if (axios.isCancel(err) || err?.code === "ERR_CANCELED") {
      const requestKey = originalRequest?._requestKey;
      if (requestKey) pendingRequestKeys.delete(requestKey);
      return new Promise(() => {});
    }

    // ✅ pending 해제
    const requestKey = originalRequest?._requestKey;
    if (requestKey) pendingRequestKeys.delete(requestKey);

    // 읽기 요청만 일시적 전송 실패를 짧게 재시도한다.
    // 결제/문자 발송 POST는 서버 처리 여부를 확정할 수 없으므로 절대 자동 재시도하지 않는다.
    const transientRetryCount = Number(
      originalRequest?._transientRetryCount ?? 0
    );
    if (
      isTransientGetError(err) &&
      transientRetryCount < MAX_TRANSIENT_GET_RETRIES
    ) {
      originalRequest._transientRetryCount = transientRetryCount + 1;
      delete originalRequest._requestKey;
      await new Promise((resolve) =>
        setTimeout(resolve, transientGetRetryDelay(transientRetryCount))
      );
      return api(originalRequest);
    }

    const failureContext = buildApiFailureContext(err);
    if (failureContext) {
      enqueueApiFailure(failureContext);
    }

    const resultCode = err.response?.data?.result_code;

    // HTTP 에러 + TOKEN_EXPIRED (드문 케이스)
    if (
      resultCode === RESULT_CODE.TOKEN_EXPIRED &&
      !(originalRequest as any)?._retry
    ) {
      (originalRequest as any)._retry = true;

      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) {
        rootStore.signOut();
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
        return new Promise(() => {});
      }

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers[HEADER.AUTH_TOKEN] = newToken;

        delete (originalRequest as any)._requestKey;

        return api(originalRequest);
      } catch {
        rootStore.signOut();
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
        return new Promise(() => {});
      }
    }

    // EMPTY_TOKEN
    if (resultCode === RESULT_CODE.EMPTY_TOKEN) {
      rootStore.signOut();
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
      return new Promise(() => {});
    }

    return Promise.reject(err);
  }
);

export default api;
